import { useEffect, useRef, useState } from 'react';

import metricsService from '../services/metrics';
import providersService from '../services/providers';
import useGandalfPostAuth from '../hooks/useGandalfPostAuth';
import TopHeader from '../components/TopHeader';
import Main from '../components/Main';
import ContainerLayout from '../components/ContainerLayout';
import Footer from '../components/Footer';
import { AppURL } from '../constants/urls';
import { IMPRESSIONS } from '../constants/metrics';
import { redirectToLXPAuth } from '../services/auth';
import EulaCheck from '../components/postAuth/EulaCheck';
import { IdProvider, PostAuthComponentParams } from '../types/auth';
import { AppConfig } from '../types/app';
import SignInUnauthorized from '../components/SignInUnauthorized';
import SignInFailed from '../components/SignInFailed';
import UnauthorizedError from '../errors/UnauthorizedError';
import { IDP_URL_PARAM } from '../constants/auth';
import MissingEmailError from '../errors/MissingEmailError';
import MissingEmail from '../components/MissingEmail';

enum PostAuthFlows {
  EULA = 'EULA',
}

type PostAuthComponent = {
  type: PostAuthFlows;
  component: React.FC<PostAuthComponentParams>;
};

/**
 * List all post auth flow components in the order they should be displayed.
 */
const postAuthFlows: PostAuthComponent[] = [
  { type: PostAuthFlows.EULA, component: EulaCheck },
];

/**
 * Replaces current URL state to enable "back" and reload in the browser.
 */
function replaceUrlWithReloadSafeParams(searchParams: string) {
  const refreshParams = new URLSearchParams(searchParams);
  // Remove the identity_provider to not trigger preferred IDP flow.
  refreshParams.delete(IDP_URL_PARAM);
  window.history.replaceState(
    {},
    document.title,
    `${AppURL.Login}?${refreshParams.toString()}`
  );
}

function AuthResponse({ config }: { config: AppConfig }) {
  const [flowIndex, setFlowIndex] = useState(0);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [provider, setProvider] = useState<IdProvider | null>(null);
  const { user, error, customState } = useGandalfPostAuth();
  const metricsPublisher = useRef(metricsService.getPublisher('AuthResponse'));
  useEffect(() => {
    metricsPublisher.current.publishCounterMonitor(IMPRESSIONS, 1);
  }, []);

  // Await auth flow and custom OIDC state, the state contains the persistes search params.
  // When this is received we can start post auth flows and then continue redirecting to
  // the LXP specific auth.
  useEffect(() => {
    if (!customState) return;
    const searchParams = customState;
    const urlSearchParams = new URLSearchParams(searchParams);
    const idp = urlSearchParams.get(IDP_URL_PARAM);
    // Update the URL
    replaceUrlWithReloadSafeParams(searchParams);
    // Get the provider. Note, it will use values from the URL.
    const provider = providersService.transformNameToIdProvider(
      idp!,
      config.gandalfDomain
    );
    setProvider(provider);
  }, [config.gandalfDomain, customState]);

  const handleContinue = () => {
    const nextFlowIndex = flowIndex + 1;
    metricsPublisher.current.publishCounterMonitor('PostAuthContinue', 1);
    // Are we ready to redirect to LXP auth?
    if (nextFlowIndex === postAuthFlows.length) {
      redirectToLXPAuth(provider!);
      setIsRedirecting(true);
    } else {
      setFlowIndex(nextFlowIndex);
    }
  };

  const handleCancel = () => {
    metricsPublisher.current.publishCounterMonitor('PostAuthCancel', 1);
    window.location.reload();
  };

  const isAuthenticatedWithProvider = Boolean(user && provider);

  if (error) {
    if (error instanceof UnauthorizedError) {
      return <SignInUnauthorized config={config} />;
    } else if (error instanceof MissingEmailError) {
      return <MissingEmail config={config} />;
    }
    return <SignInFailed error={error} />;
  }

  if (isRedirecting) return null;
  if (!isAuthenticatedWithProvider) return null;

  const AuthFlowComponent = postAuthFlows[flowIndex].component;
  return (
    <>
      <TopHeader />
      <Main>
        <ContainerLayout>
          <AuthFlowComponent
            user={user!}
            onContinue={handleContinue}
            onCancel={handleCancel}
            config={config}
          />
        </ContainerLayout>
      </Main>
      <Footer />
    </>
  );
}

export default AuthResponse;
